import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import get from 'lodash/get';
import { useGuidesStore } from '@/stores/guides.store';
import { ExperimentsService } from '@/guides/services/experiments-service';
import { createVariantUpdate, validateVariantUpdate } from '@/utils/experiment.utils';
import { getDefaultSegment } from '@utils/segments';
import { buildAudiencePipeline } from '@/guides/helpers/common.util';
import { experimentActivatedTrackEvent } from '@/guides/track-events/experiments/experiment-activated';

let loadAllPromise = null;

const experiments = {
    namespaced: true,
    state: {
        experiments: {},
        experiment: null,
        variantResults: null,
        loading: false,
        saving: false,
        error: null,
    },
    mutations: {
        setExperiments (state, experimentsArray) {
            state.experiments = keyBy(experimentsArray, 'id');
        },
        setExperiment (state, experiment) {
            state.experiment = experiment;
        },
        addExperiment (state, experiment) {
            state.experiments[experiment.id] = experiment;
        },
        updateExperiment (state, updatedExperiment) {
            if (state.experiments[updatedExperiment.id]) {
                state.experiments[updatedExperiment.id] = updatedExperiment;
            }

            if (state.experiment && state.experiment.id === updatedExperiment.id) {
                state.experiment = updatedExperiment;
            }
        },
        removeExperiment (state, experimentId) {
            delete state.experiments[experimentId];
            if (state.experiment && state.experiment.id === experimentId) {
                state.experiment = null;
            }
        },
        setLoading (state, isLoading) {
            state.loading = isLoading;
        },
        setSaving (state, isSaving) {
            state.saving = isSaving;
        },
        setError (state, error) {
            state.error = error;
        },
        setVariantResults (state, results) {
            state.variantResults = results;
        },
    },
    actions: {
        async fetchAllExperiments ({ commit }, { reFetch = false } = {}) {
            if (loadAllPromise && !reFetch) {
                try {
                    const response = await loadAllPromise;

                    return response.data;
                } catch (error) {
                    throw error;
                }
            }

            commit('setLoading', true);
            commit('setError', null);

            const currentFetchPromise = ExperimentsService.getExperiments();
            loadAllPromise = currentFetchPromise;

            try {
                const response = await currentFetchPromise;

                if (loadAllPromise === currentFetchPromise) {
                    commit('setExperiments', response.data);
                }

                return response.data;
            } catch (error) {
                if (loadAllPromise === currentFetchPromise) {
                    loadAllPromise = null;
                }
                commit('setError', error);
                throw error;
            } finally {
                if (loadAllPromise === currentFetchPromise) {
                    commit('setLoading', false);
                }
            }
        },

        async fetchExperiment ({ commit, getters }, { id, reFetch = false }) {
            const loadedExperiment = getters.getExperimentById(id);
            if (!reFetch && loadedExperiment) return Promise.resolve(loadedExperiment);
            commit('setLoading', true);
            commit('setError', null);

            try {
                const response = await ExperimentsService.getExperiment(id);
                commit('setExperiment', response.data);

                return response.data;
            } catch (error) {
                commit('setError', error);
                throw error;
            } finally {
                commit('setLoading', false);
            }
        },

        async createExperiment ({ commit }, { experiment }) {
            commit('setSaving', true);
            commit('setError', null);

            try {
                const response = await ExperimentsService.createExperiment(experiment);
                commit('addExperiment', response.data);
                commit('setExperiment', response.data);

                return response.data;
            } catch (error) {
                commit('setError', error);
                throw error;
            } finally {
                commit('setSaving', false);
            }
        },

        async updateExperiment ({ commit, getters }, { id, experiment, options = {} }) {
            commit('setSaving', true);
            commit('setError', null);

            const { variantUpdates = false, statusUpdate = false } = options;
            const previousVariantIds = getters.getExperimentById(id)?.variants?.map(({ guideId }) => guideId) || [];

            try {
                const { data: updatedExperiment } = await ExperimentsService.updateExperiment(id, experiment);
                commit('updateExperiment', updatedExperiment);

                if (variantUpdates || statusUpdate) {
                    // Collect guide IDs from both current state and new state
                    // to ensure removed variants are also refreshed
                    const newVariantIds = updatedExperiment.variants?.map(({ guideId }) => guideId) || [];

                    // Create a unique set of all guide IDs that need refreshing
                    const guideIdsToRefresh = [...new Set([...previousVariantIds, ...newVariantIds])].filter(Boolean);

                    if (guideIdsToRefresh.length > 0) {
                        // Reload all guides that were or are part of the experiment
                        const guideReloadPromises = guideIdsToRefresh.map((guideId) =>
                            useGuidesStore().loadOne({ id: guideId, reFetch: true }),
                        );
                        await Promise.all(guideReloadPromises);
                    }
                }

                if (statusUpdate && updatedExperiment.state === 'active') {
                    experimentActivatedTrackEvent(updatedExperiment);
                }

                return updatedExperiment;
            } catch (error) {
                commit('setError', error);
                throw error;
            } finally {
                commit('setSaving', false);
            }
        },

        async deleteExperiment ({ commit }, { id }) {
            commit('setSaving', true);
            commit('setError', null);

            try {
                await ExperimentsService.deleteExperiment(id);
                commit('removeExperiment', id);

                return true;
            } catch (error) {
                commit('setError', error);
                throw error;
            } finally {
                commit('setSaving', false);
            }
        },

        async updateVariant ({ dispatch, getters }, { id, variant, guide, segment }) {
            const experiment = getters.getExperimentById(id);
            const newVariants = createVariantUpdate(guide.id, variant, experiment.variants);
            const appId = validateVariantUpdate(experiment.state, newVariants, useGuidesStore().map);
            let newAudience = {};
            if (appId) {
                newAudience = await dispatch('createAudienceUpdate', { appId, experiment, segment });
            }

            await dispatch('updateExperiment', {
                id,
                experiment: {
                    ...experiment,
                    ...newAudience,
                    appId,
                    variants: newVariants,
                },
                options: {
                    variantUpdates: true,
                },
            });
        },

        async createAudienceUpdate ({ rootGetters, _dispatch }, { appId, experiment, segment }) {
            const app = rootGetters['apps/getAppById'](appId);
            if (!app?.id) {
                throw new Error('Cannot update experiment audience without a valid app');
            }
            if (app.id === experiment.appId) return null;

            // Rebuild audience pipeline if app ID has changed since the app is what controls
            // if guides are shown to anonymous users.
            const segmentId = get(experiment, 'audienceUiHint.filters[0].segmentId', 'everyone');
            let newSegment = segment;

            // if a segment is not passed in, get the segment id from the experiment
            if (!newSegment) {
                newSegment = getDefaultSegment(segmentId);
            }

            // if the experiment segment is not a default segment, load the segment
            if (!newSegment) {
                // eslint-disable-next-line no-warning-comments
                const { useSegmentsStore } = require('@/stores/segments.store'); // TODO: remove when experiments migrates to Pinia
                const segmentsStore = useSegmentsStore();
                await segmentsStore.loadOne({ id: segmentId });
                newSegment = segmentsStore.segmentById(segmentId);
            }

            const { audience, audienceUiHint } = await buildAudiencePipeline(newSegment, app);

            return { audience, audienceUiHint };
        },
    },
    getters: {
        isLoading: (state) => state.loading,
        isSaving: (state) => state.saving,
        hasError: (state) => !!state.error,
        getError: (state) => state.error,
        getAllExperiments: (state) => values(state.experiments),
        getCurrentExperiment: (state) => state.experiment,
        getExperimentById: (state) => (id) => {
            return state.experiments[id];
        },
    },
};

export const __resetLoadAllPromiseForUnitTests = () => {
    loadAllPromise = null;
};

export default experiments;
